import React, { useState } from 'react';
import { Select, MenuItem, Box, TextField, IconButton } from '@mui/material';
import {
  ApiOutlined,
  FmdGoodOutlined,
  LocalPhoneOutlined,
  MailOutlined,
  OpenInNewRounded,
  TrackChangesOutlined,
  Search
} from '@mui/icons-material';
import { CompanyDetailItem } from './RenderDynamicContent.jsx';
import { useDispatch } from 'react-redux';
import { setSelectedApplicationIdData } from '../../store/ApplicationSlice.js';

function SideBarContent ({
  company,
  fetchImgData,
  uniqueValues,
  setIsLoading,
  setLogoUrl,
  logoUrl,
  applicationData,
  getWebsiteUrl,
  mapRef,
  onCompanyChange // Add this prop
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newCompanyName, setNewCompanyName] = useState('');
  const [selCompany, setSelCompany] = useState(company);
  const company_details = applicationData?.analysis?.company_details;

  const onSelectCompany = async company => {
    if (!company) return;
    setSelCompany(company);
    setOpen(false);

    if (company !== 'New Company') {
      await onCompanyChange(company, false);
    } else {
      dispatch(setSelectedApplicationIdData(null));
      setLogoUrl('');
    }
  };

  const onSearchClick = async () => {
    if (!newCompanyName.trim()) return;
    await onCompanyChange(newCompanyName, true);
    setNewCompanyName(''); // Clear search field after search
  };

  return (
    <div className='w-full lg:w-1/3 xl:w-1/4 space-y-2'>
      <Select
        size='small'
        variant='standard'
        value={selCompany}
        disableUnderline
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        className='w-full bg-white pt-2 pb-1 px-3 rounded-lg'
        renderValue={selected => selected || 'Select company'}
        MenuProps={{
          disableEnforceFocus: true, // ✅ Prevents dropdown from closing on refocus
          PaperProps: {
            sx: { maxHeight: 300, width: 250 }
          }
        }}
      >
        <div style={{ padding: '8px' }} onClick={e => e.stopPropagation()}>
          <TextField
            size='small'
            placeholder='Search...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            autoFocus
            sx={{ width: '100%', fontSize: '14px' }}
            onClick={e => e.stopPropagation()} // ✅ Stops closing on click
            onMouseDown={e => e.stopPropagation()} // ✅ Prevents dropdown from closing on mouse down
          />
        </div>

        <Box>
          {[
            'New Company',
            ...uniqueValues['customer_name'].filter(name =>
              name.toLowerCase().includes(searchTerm.toLowerCase())
            )
          ].map((name, index) => (
            <MenuItem
              sx={{ fontSize: '14px' }}
              key={index} // Using `index` for uniqueness since "New Company" might not be in `uniqueValues`
              value={name}
              onClick={() => onSelectCompany(name)}
            >
              {name}
            </MenuItem>
          ))}
        </Box>
      </Select>
      {selCompany === 'New Company' && (
        <div className='flex items-center justify-between bg-white py-2 px-3 rounded-lg'>
          <TextField
            className='w-full'
            size='small'
            label='Enter new company'
            variant='outlined'
            placeholder='Enter new company'
            value={newCompanyName}
            onChange={e => setNewCompanyName(e.target.value)}
            sx={{ width: '100%' }}
          />
          <IconButton style={{ color: '#FF4D00' }} onClick={onSearchClick}>
            <Search />
          </IconButton>
        </div>
      )}

      <div className='p-2 bg-white rounded-lg'>
        <div className='flex items-center gap-2'>
          <div className='flex-none flex items-center justify-center w-[90px] h-[90px] bg-gray-200 rounded-full overflow-hidden'>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt='Logo'
                className='object-contain w-full h-full'
              />
            ) : (
              <img
                src={'/img/images.png'}
                alt='Logo'
                className='object-contain w-full h-full'
              />
            )}
          </div>
          <div className=' space-y-1'>
            <h2 className='text-base font-semibold'>
              {applicationData?.company_name}
            </h2>
            {company_details?.website && (
              <a
                href={getWebsiteUrl(company_details.website)}
                target='_blank'
                rel='noopener noreferrer'
                className=' text-gray-400 hover:underline inline-flex items-center gap-1 max-w-xs break-words break-all'
              >
                <span className='break-all'>
                  Website{' '}
                  <OpenInNewRounded
                    sx={{ fontSize: 16 }}
                    className='shrink-0'
                  />
                </span>
              </a>
            )}
          </div>
        </div>
      </div>

      <div className='p-4 space-y-4 bg-white rounded-lg'>
        <div className='space-y-1'>
          <h2 className='text-lg font-semibold'>Company Summary</h2>
          <div className='flex items-center gap-2 text-gray-500'>
            {applicationData?.company_name}
          </div>
        </div>
        <div className=' space-y-3'>
          <CompanyDetailItem
            icon={<LocalPhoneOutlined sx={{ fontSize: 16 }} />}
            value={company_details?.contact_number}
          />
          <CompanyDetailItem
            icon={<MailOutlined sx={{ fontSize: 16 }} />}
            value={company_details?.email_address}
          />
          <CompanyDetailItem
            icon={<FmdGoodOutlined sx={{ fontSize: 16 }} />}
            value={company_details?.address}
          />
          <CompanyDetailItem
            icon={<TrackChangesOutlined sx={{ fontSize: 16 }} />}
            value={company_details?.naics}
          />
        </div>
        <div ref={mapRef} className='w-full h-32 bg-gray-200 rounded-lg'></div>
      </div>

      <div className='p-4 bg-white rounded-lg'>
        <h3 className='text-md font-semibold mb-2'>APIs</h3>
        <div className='flex gap-4'>
          {Array(3)
            .fill(null)
            .map((_, index) => (
              <div
                key={index}
                className='flex items-center p-4 bg-gray-200 rounded-full'
              >
                <ApiOutlined sx={{ fontSize: 20 }} />
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default SideBarContent;
